import { StandardEvents } from '@shopify/events';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Amada option → gallery media matching.
 *
 * Catalogue reality (e.g. solitaire with 10 shapes × metals × carats):
 *   - Hundreds of variants, usually with no featured_media attached
 *   - Image alt text is often blank; shape/metal lives in the filename
 *     (OVAL.jpg, ROUNDYELLOW.jpg, HEART.jpg, …)
 *
 * On productSelect, filter the PDP gallery to media whose alt or filename
 * matches the selected shape / metal family. Carat / size options are ignored.
 */

const SHAPE_OPTION = /diamond\s*shape|^\s*shape\s*$|^\s*cut\s*$/i;
const METAL_OPTION = /metal/i;

const SHAPES = [
  'marquise',
  'princess',
  'emerald',
  'radiant',
  'asscher',
  'cushion',
  'heart',
  'round',
  'oval',
  'pear',
];

/** @param {string} value */
function compact(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** @param {string} src */
function filenameFromSrc(src) {
  if (!src) return '';
  const path = src.split('?')[0] || '';
  const file = path.split('/').pop() || '';
  return file.replace(/\.[^.]+$/, '');
}

/** @param {Element} root */
function mediaHaystack(root) {
  const img =
    root.querySelector('img.product-media__image') ||
    root.querySelector('img') ||
    root.querySelector('model-viewer');
  const alt = img?.getAttribute('alt') || '';
  const src =
    img?.getAttribute('src') || img?.getAttribute('data-src') || /** @type {HTMLImageElement} */ (img)?.currentSrc || '';
  return compact(`${alt} ${filenameFromSrc(src)}`);
}

/**
 * @param {string} haystack
 * @param {string} shape
 */
function matchesShape(haystack, shape) {
  const token = compact(shape);
  if (!token || !haystack) return false;
  if (haystack.startsWith(token)) return true;
  return SHAPES.includes(token) && haystack.includes(token);
}

/** @param {string} metalValue */
function metalFamily(metalValue) {
  const m = (metalValue || '').toLowerCase();
  if (!m) return null;
  if (/rose/.test(m)) return /** @type {const} */ ('rose');
  if (/yellow/.test(m)) return /** @type {const} */ ('yellow');
  if (/platinum/.test(m)) return /** @type {const} */ ('platinum');
  if (/white|palladium/.test(m)) return /** @type {const} */ ('white');
  return null;
}

/**
 * Prefer canonical alt tags: amada-metal:white|yellow|rose|platinum
 * (compacted haystack → amadametalwhite etc). Filename heuristics are fallback only.
 *
 * @param {string} haystack
 * @param {'yellow' | 'rose' | 'white' | 'platinum'} family
 */
function matchesMetal(haystack, family) {
  if (!haystack) return false;
  const tag = `amadametal${family}`;
  if (haystack.includes(tag)) return true;

  const hasYellow = haystack.includes('yellow');
  const hasRose = haystack.includes('rose');
  const hasPlatinum = haystack.includes('platinum');
  const hasWhite = haystack.includes('white');

  if (family === 'yellow') return hasYellow;
  if (family === 'rose') return hasRose;
  if (family === 'platinum') return hasPlatinum || (hasWhite && !hasYellow && !hasRose);
  // White gold: tagged white, or unmarked (not yellow/rose/platinum-only).
  if (hasWhite && !hasYellow && !hasRose) return true;
  return !hasYellow && !hasRose && !hasPlatinum;
}

/** @param {ParentNode} scope */
function readSelectedOptions(scope) {
  const picker = scope.querySelector('variant-picker, swatches-variant-picker-component');
  /** @type {{ shape: string | null, metal: string | null }} */
  const selected = { shape: null, metal: null };
  if (!picker) return selected;

  for (const input of picker.querySelectorAll('input:checked')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const optionName = input.dataset.optionName || input.closest('fieldset')?.querySelector('legend')?.textContent || '';
    const value = (input.value || input.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
    if (!value) continue;

    if (SHAPE_OPTION.test(optionName)) selected.shape = value;
    else if (METAL_OPTION.test(optionName)) selected.metal = value;
  }

  for (const select of picker.querySelectorAll('select')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    const optionName =
      select.dataset.optionName ||
      select.getAttribute('name') ||
      select.closest('label, fieldset')?.querySelector('legend, .variant-option__label')?.textContent ||
      '';
    const value = select.selectedOptions[0]?.textContent?.trim() || select.value;
    if (!value) continue;
    if (SHAPE_OPTION.test(optionName)) selected.shape = value;
    else if (METAL_OPTION.test(optionName)) selected.metal = value;
  }

  return selected;
}

/**
 * @param {Array<{ el: Element, haystack: string }>} items
 * @param {{ shape: string | null, metal: string | null }} selected
 */
function pickMatches(items, selected) {
  const family = metalFamily(selected.metal || '');
  const shape = selected.shape;

  if (!shape && !family) return new Set(items.map((i) => i.el));

  const scored = items.map(({ el, haystack }) => ({
    el,
    haystack,
    shapeOk: shape ? matchesShape(haystack, shape) : true,
    metalOk: family ? matchesMetal(haystack, family) : true,
  }));

  if (shape && family) {
    const both = scored.filter((s) => s.shapeOk && s.metalOk);
    if (both.length) return new Set(both.map((s) => s.el));
  }

  if (shape) {
    const shapeHits = scored.filter((s) => s.shapeOk);
    if (shapeHits.length) return new Set(shapeHits.map((s) => s.el));
  }

  if (family) {
    const tagged = scored.filter(({ haystack, metalOk }) => {
      if (!metalOk) return false;
      // Prefer canonical amada-metal:* tags when present anywhere in the gallery.
      if (haystack.includes(`amadametal${family}`)) return true;
      if (family === 'yellow') return haystack.includes('yellow');
      if (family === 'rose') return haystack.includes('rose');
      if (family === 'platinum') return haystack.includes('platinum') || haystack.includes('white');
      return haystack.includes('white') || (!haystack.includes('yellow') && !haystack.includes('rose'));
    });
    if (tagged.length) return new Set(tagged.map((s) => s.el));
  }

  return new Set(items.map((i) => i.el));
}

class AmadaVariantMedia extends HTMLElement {
  /** @type {ParentNode | null} */
  #root = null;
  /** @type {((event: Event) => void) | null} */
  #onProductSelect = null;
  /** @type {((event: Event) => void) | null} */
  #onSlideshowSelect = null;
  /**
   * Original control nodes per slideshow, kept for restore between filters.
   * @type {WeakMap<Element, { thumbnails: Element[], dots: Element[], thumbParent: Element | null, dotsParent: Element | null }>}
   */
  #controlOriginals = new WeakMap();

  connectedCallback() {
    const root = this.closest('.shopify-section, [id^="shopify-section-"]');
    if (!root) return;
    this.#root = root;

    if (!root.querySelector('variant-picker, swatches-variant-picker-component')) return;
    if (!root.querySelector('media-gallery')) return;

    this.#onProductSelect = (event) => {
      const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
      if (!promise) {
        this.#apply();
        return;
      }
      promise
        .then(() => this.#apply())
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[amada-variant-media] product select rejected:', error);
          }
        });
    };
    root.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#onSlideshowSelect = () => this.#syncThumbnailSelection();
    root.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);

    this.#apply();
  }

  disconnectedCallback() {
    if (this.#onProductSelect) {
      this.#root?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    }
    if (this.#onSlideshowSelect) {
      this.#root?.removeEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect);
    }
    this.#restoreAllControls();
  }

  #apply() {
    const root = this.#root;
    if (!root) return;

    const gallery = root.querySelector('media-gallery');
    if (!gallery) return;

    const selected = readSelectedOptions(root);
    if (!selected.shape && !selected.metal) {
      this.#restoreAllControls();
      this.#setHidden(gallery, null);
      return;
    }

    const slideNodes = [...gallery.querySelectorAll('slideshow-slide.product-media-container')];
    const gridNodes = [...gallery.querySelectorAll('.media-gallery__grid > li.product-media-container')];
    const primary = slideNodes.length ? slideNodes : gridNodes;
    if (!primary.length) return;

    const items = primary.map((el) => ({ el, haystack: mediaHaystack(el) }));
    const anyTagged = items.some(
      ({ haystack }) => SHAPES.some((s) => haystack.includes(s)) || /yellow|rose|white|platinum/.test(haystack)
    );
    if (!anyTagged) return;

    const matches = pickMatches(items, selected);
    this.#setHidden(gallery, matches, slideNodes, gridNodes);
    this.#syncControls(gallery, slideNodes, matches.size === items.length);
    this.#selectFirstVisible(gallery);
  }

  /**
   * @param {Element} gallery
   * @param {Set<Element> | null} matches
   * @param {Element[]} [slideNodes]
   * @param {Element[]} [gridNodes]
   */
  #setHidden(gallery, matches, slideNodes = [], gridNodes = []) {
    const apply = (nodes, sourceNodes = nodes) => {
      nodes.forEach((el, i) => {
        const key = sourceNodes[i] || el;
        // if (!matches || matches.has(key)) el.removeAttribute('hidden');
        // else el.setAttribute('hidden', '');
      });
    };

    if (!matches) {
      gallery
        .querySelectorAll(
          'slideshow-slide.product-media-container[hidden], .media-gallery__grid > li.product-media-container[hidden], .dialog-zoomed-gallery > li.product-media-container[hidden], .dialog-thumbnails-list__thumbnail[hidden]'
        )
        .forEach((el) => el.removeAttribute('hidden'));
      return;
    }

    apply(slideNodes);
    if (slideNodes.length && gridNodes.length) apply(gridNodes, slideNodes);
    else apply(gridNodes);

    const zoomSlides = [...gallery.querySelectorAll('.dialog-zoomed-gallery > li.product-media-container')];
    const zoomThumbs = [...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
    if (slideNodes.length) {
      apply(zoomSlides, slideNodes);
      apply(zoomThumbs, slideNodes);
    }
  }

  /**
   * Detach thumbnails/dots for hidden slides so slideshow refs stay aligned
   * with the visible-slides getter (index 0 === first visible thumb).
   * @param {Element} gallery
   * @param {Element[]} slides
   * @param {boolean} showingAll
   */
  #syncControls(gallery, slides, showingAll) {
    for (const slideshow of gallery.querySelectorAll('slideshow-component')) {
      this.#ensureControlSnapshot(slideshow);
      this.#restoreControlGroup(slideshow);

      if (showingAll || !slides.length) continue;

      const snapshot = this.#controlOriginals.get(slideshow);
      if (!snapshot) continue;

      snapshot.thumbnails.forEach((node, i) => {
        if (slides[i]?.hasAttribute('hidden')) node.remove();
      });
      snapshot.dots.forEach((node, i) => {
        if (slides[i]?.hasAttribute('hidden')) node.remove();
      });
    }
  }

  /** @param {Element} slideshow */
  #ensureControlSnapshot(slideshow) {
    if (this.#controlOriginals.has(slideshow)) return;

    const thumbnails = [...slideshow.querySelectorAll('[ref="thumbnails[]"]')];
    const dots = [...slideshow.querySelectorAll('[ref="dots[]"]')];
    this.#controlOriginals.set(slideshow, {
      thumbnails,
      dots,
      thumbParent: thumbnails[0]?.parentElement || null,
      dotsParent: dots[0]?.parentElement || null,
    });
  }

  /** @param {Element} slideshow */
  #restoreControlGroup(slideshow) {
    const snapshot = this.#controlOriginals.get(slideshow);
    if (!snapshot) return;

    if (snapshot.thumbParent) {
      for (const node of snapshot.thumbnails) {
        if (!node.isConnected) snapshot.thumbParent.appendChild(node);
      }
      // Re-order to original sequence.
      for (const node of snapshot.thumbnails) snapshot.thumbParent.appendChild(node);
    }

    if (snapshot.dotsParent) {
      for (const node of snapshot.dots) {
        if (!node.isConnected) snapshot.dotsParent.appendChild(node);
      }
      for (const node of snapshot.dots) snapshot.dotsParent.appendChild(node);
    }
  }

  #restoreAllControls() {
    const root = this.#root;
    if (!root) return;
    for (const slideshow of root.querySelectorAll('slideshow-component')) {
      this.#restoreControlGroup(slideshow);
    }
  }

  /** @param {Element} gallery */
  #selectFirstVisible(gallery) {
    const slideshow = /** @type {any} */ (gallery.querySelector('slideshow-component'));
    if (!slideshow?.select) return;

    Promise.resolve(slideshow.select(0, undefined, { animate: false }))
      .then(() => this.#syncThumbnailSelection())
      .catch(() => {});
  }

  #syncThumbnailSelection() {
    const root = this.#root;
    if (!root) return;

    for (const slideshow of root.querySelectorAll('slideshow-component')) {
      const typed = /** @type {any} */ (slideshow);
      const index = typed.current ?? 0;
      const thumbnails = [...slideshow.querySelectorAll('[ref="thumbnails[]"]')];
      thumbnails.forEach((el, i) => el.setAttribute('aria-selected', `${i === index}`));
    }
  }
}

if (!customElements.get('amada-variant-media')) {
  customElements.define('amada-variant-media', AmadaVariantMedia);
}
